import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { ApplicationState, AppState } from '../../../store';
import { withSnackbar, ProviderContext } from 'notistack';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography
} from '@material-ui/core';
import { ScaleLoader } from 'react-spinners';
import MUIDataTable, { SelectableRows } from 'mui-datatables';
import { MuiPickersUtilsProvider, DatePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import FileSaver from 'file-saver';
import { CloudDownloadOutlined } from '@material-ui/icons';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { MUITranslations } from '../../../helpers/MUITableTranslations';
import { LanguageCode } from '../../../helpers/Constants';
import { CopyButton } from '../../../custom-forms';
import {
  IntegrationApiLogDto,
  IntegrationApiLogFilterRequestDto,
  UserInformationDto
} from '../../../interfaces/Organization';

class LocalizedUtils extends MomentUtils {}

interface IOrganizationIntegrationApiLogsState {
  logs: IntegrationApiLogDto[];
  isLoading: boolean;
  isExporting: boolean;
  page: number;
  pageSize: number;
  count: number;
  startDate: Date | null;
  endDate: Date | null;
}

type IOrganizationIntegrationApiLogsProps = { appState: AppState } & ProviderContext &
  RouteComponentProps<{ id: string }>;

class OrganizationIntegrationApiLogs extends React.PureComponent<
  IOrganizationIntegrationApiLogsProps,
  IOrganizationIntegrationApiLogsState
> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;

  static contextType = ApplicationContext;

  private get organizationId(): number {
    return Number.parseInt(this.props.match.params.id);
  }

  state = {
    logs: [],
    isLoading: false,
    isExporting: false,
    page: 0,
    pageSize: 10,
    count: 0,
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1)),
    endDate: new Date()
  } as IOrganizationIntegrationApiLogsState;

  public componentDidMount() {
    this.loadLogs(0, this.state.pageSize);
  }

  private buildFilter = (
    page: number,
    pageSize: number | null
  ): IntegrationApiLogFilterRequestDto => {
    return {
      organizationId: this.organizationId,
      startDate: this.state.startDate ? moment(this.state.startDate).startOf('day').toDate() : null,
      endDate: this.state.endDate ? moment(this.state.endDate).endOf('day').toDate() : null,
      page: page + 1,
      pageSize: pageSize,
      language: this.props.appState.language == LanguageCode.RO ? LanguageCode.RO : LanguageCode.EN
    };
  };

  loadLogs = async (page: number, pageSize: number) => {
    this.setState({ isLoading: true });
    try {
      const response = await this.organizationService.GetIntegrationApiLogList(
        this.buildFilter(page, pageSize)
      );
      this.setState({
        logs: response.integrationApiLogs,
        count: response.total,
        page: response.page - 1,
        pageSize: pageSize
      });
    } catch {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handleStartDateChange = (date: MaterialUiPickersDate | null) => {
    if (date === null || !date.isValid()) {
      return;
    }
    this.setState({ startDate: date.toDate() }, () => this.loadLogs(0, this.state.pageSize));
  };

  handleEndDateChange = (date: MaterialUiPickersDate | null) => {
    if (date === null || !date.isValid()) {
      return;
    }
    this.setState({ endDate: date.toDate() }, () => this.loadLogs(0, this.state.pageSize));
  };

  handleExport = async () => {
    this.setState({ isExporting: true });
    try {
      const blob = await this.organizationService.GetIntegrationApiLogExport(
        this.buildFilter(this.state.page, null)
      );
      FileSaver.saveAs(
        blob,
        `${this.translatorService.Tranlate(
          'INTEGRATION_API_LOG_EXPORT_FILE_NAME',
          'Loguri transmitere'
        )}.xlsx`
      );
    } catch {
      this.props.enqueueSnackbar(this.translatorService.Tranlate('ERROR_MSG', 'Eroare'), {
        variant: 'error'
      });
    } finally {
      this.setState({ isExporting: false });
    }
  };

  renderDatePicker = (
    id: string,
    label: string,
    value: Date | null,
    onChange: (date: MaterialUiPickersDate | null) => void
  ) => (
    <MuiPickersUtilsProvider
      libInstance={moment}
      utils={LocalizedUtils}
      locale={this.props.appState.language}
    >
      <DatePicker
        fullWidth
        disableToolbar
        id={id}
        variant="inline"
        format={this.props.appState.dateFormat.toUpperCase()}
        margin="none"
        label={label}
        value={value}
        onChange={onChange}
        autoOk={true}
      />
    </MuiPickersUtilsProvider>
  );

  renderFilters = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        {this.renderDatePicker(
          'integration-log-date-from',
          this.translatorService.Tranlate('INTEGRATION_API_LOG_START_DATE', 'Data inceput'),
          this.state.startDate,
          this.handleStartDateChange
        )}
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        {this.renderDatePicker(
          'integration-log-date-to',
          this.translatorService.Tranlate('INTEGRATION_API_LOG_END_DATE', 'Data sfarsit'),
          this.state.endDate,
          this.handleEndDateChange
        )}
      </Grid>
    </Grid>
  );

  formatBody = (value: string | null) => {
    if (!value) {
      return '';
    }
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch {
      return value;
    }
  };

  notifyCopied = () => {
    this.props.enqueueSnackbar(this.translatorService.Tranlate('COPIED', 'Copiat'), {
      variant: 'success'
    });
  };

  renderCopyButton = (value: string | null) =>
    value ? (
      <CopyButton
        value={this.formatBody(value)}
        tooltip={this.translatorService.Tranlate('COPY', 'Copiază')}
        onCopied={() => this.notifyCopied()}
      />
    ) : null;

  renderBodyCell = (value: string | null) =>
    value ? (
      <Box display="flex" alignItems="center" maxWidth={250}>
        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{value}</span>
        {this.renderCopyButton(value)}
      </Box>
    ) : (
      ''
    );

  renderBody = (title: string, value: string | null) => (
    <Grid item xs={6}>
      <Box
        display="flex"
        flexDirection="column"
        height="100%"
        border="1px solid #d1d2db"
        borderRadius={8}
        overflow="hidden"
        bgcolor="#fff"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          px={2}
          minHeight={40}
          bgcolor="#f4f5fd"
          borderBottom="1px solid #d1d2db"
        >
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {title}
          </Typography>
          {this.renderCopyButton(value)}
        </Box>
        <pre
          style={{
            flex: 1,
            margin: 0,
            padding: 16,
            minHeight: 120,
            maxHeight: 360,
            overflow: 'auto',
            fontFamily: 'inherit',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            color: value ? 'inherit' : '#8e8ea0'
          }}
        >
          {this.formatBody(value) || '-'}
        </pre>
      </Box>
    </Grid>
  );

  renderExpandableRow = (rowData: string[], rowMeta: { dataIndex: number }) => {
    const log = this.state.logs[rowMeta.dataIndex];
    return (
      <TableRow>
        <TableCell colSpan={rowData.length + 1} style={{ background: '#fafbfc' }}>
          <Box maxWidth={1200} py={1}>
            <Grid container spacing={2} alignItems="stretch">
              {this.renderBody('Request', log.requestBody)}
              {this.renderBody('Response', log.responseBody)}
            </Grid>
          </Box>
        </TableCell>
      </TableRow>
    );
  };

  getColumns = () => [
    {
      name: 'id',
      label: 'Id'
    },
    {
      name: 'organizationId',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_ORGANIZATION', 'Organizatie')
    },
    {
      name: 'caseId',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_CASE', 'Dosar'),
      options: {
        customBodyRender: (value: number | null) =>
          value ? (
            <Link to={`/cases/${value}`} onClick={(event) => event.stopPropagation()}>
              {value}
            </Link>
          ) : (
            ''
          )
      }
    },
    {
      name: 'triggerCode',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_EVENT', 'Eveniment')
    },
    {
      name: 'route',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_ROUTE', 'Rută')
    },
    {
      name: 'method',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_METHOD', 'Metodă')
    },
    {
      name: 'requestBody',
      label: 'Request',
      options: {
        customBodyRender: (value: string | null) => this.renderBodyCell(value)
      }
    },
    {
      name: 'responseBody',
      label: 'Response',
      options: {
        customBodyRender: (value: string | null) => this.renderBodyCell(value)
      }
    },
    {
      name: 'statusCode',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_STATUS_CODE', 'Status code')
    },
    {
      name: 'success',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_SUCCESS', 'Succes'),
      options: {
        customBodyRender: (value: boolean) =>
          value ? (
            <CheckCircleOutlineIcon className="text-success" />
          ) : (
            <HighlightOffIcon color="error" />
          )
      }
    },
    {
      name: 'errorMessage',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_ERROR', 'Eroare'),
      options: {
        customBodyRender: (value: string | null) => value || ''
      }
    },
    {
      name: 'dateCreation',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_DATE', 'Data'),
      options: {
        customBodyRender: (value: string | null) =>
          value ? moment.utc(value).local().format(this.props.appState.longDateFormat) : ''
      }
    },
    {
      name: 'createdByUser',
      label: this.translatorService.Tranlate('INTEGRATION_API_LOG_CREATED_BY', 'Creat de'),
      options: {
        customBodyRender: (value: UserInformationDto | null) => value?.email || ''
      }
    }
  ];

  public render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;

    const options = {
      filter: false,
      search: false,
      selectableRows: 'none' as SelectableRows,
      selectableRowsOnClick: false,
      print: false,
      download: false,
      textLabels: MUITranslations.GetTranslations(this.translatorService),
      sort: false,
      viewColumns: false,
      elevation: 0,
      serverSide: true,
      count: this.state.count,
      rowsPerPage: this.state.pageSize,
      page: this.state.page,
      expandableRows: true,
      expandableRowsOnClick: true,
      renderExpandableRow: this.renderExpandableRow,
      onTableChange: (action: string, tableState: any) => {
        if (action === 'changePage' || action === 'changeRowsPerPage') {
          this.loadLogs(tableState.page, tableState.rowsPerPage);
        }
      },
      customToolbar: () => (
        <Fragment>
          {this.state.isExporting ? (
            <CircularProgress size={20} color="primary" style={{ verticalAlign: 'middle' }} />
          ) : null}
          <Tooltip title={this.translatorService.Tranlate('Export', 'Exporta')}>
            <span>
              <IconButton
                aria-label="export"
                color="primary"
                className="m-2"
                disabled={this.state.isExporting}
                onClick={() => this.handleExport()}
              >
                <CloudDownloadOutlined />
              </IconButton>
            </span>
          </Tooltip>
        </Fragment>
      )
    };

    return (
      <Box mt={1} pt={1}>
        <Card>
          <CardContent>
            <Box mb={2}>{this.renderFilters()}</Box>
            <div className="d-flex flex-row text-center flex-wrap justify-content-center">
              <ScaleLoader color={'var(--primary)'} loading={this.state.isLoading} />
            </div>
            {!this.state.isLoading ? (
              <MUIDataTable
                title={''}
                data={this.state.logs}
                columns={this.getColumns()}
                options={options}
              />
            ) : null}
          </CardContent>
        </Card>
      </Box>
    );
  }
}

export default connect(
  (state: ApplicationState) => ({
    appState: state.app
  }),
  null
)(withSnackbar(OrganizationIntegrationApiLogs as any));
